import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  addDays,
  isBefore,
  startOfDay,
  parseISO,
  eachMinuteOfInterval,
  setHours,
  setMinutes
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronLeft,
  ChevronRight,
  User,
  CheckCircle2,
  Globe,
  Briefcase,
  Video,
  Timer,
  ArrowLeft,
  Download,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { TIMEZONES } from '../constants/timezones';
import './CalendarPage.css';

import { API_BASE_URL } from '../config';

// const API_BASE_URL = "https://screening.fanisko.com";
// const API_BASE_URL = "http://127.0.0.1:8000";
const FETCH_DAYS = 2; // RESTORED from original
const SLOT_DURATION = 30;
const SLOT_INCREMENT = 30;
const WORKING_HOURS = { start: 9, end: 18 };
const DEFAULT_RECRUITER_EMAIL = "koratchirag90@gmail.com";

const CalendarPage = () => {
  const navigate = useNavigate();
  // Navigation & Step State
  const [step, setStep] = useState('select'); // 'select', 'confirm', 'success'
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Selection State
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz === 'Asia/Calcutta' ? 'Asia/Kolkata' : tz;
  });
  const [is24Hour, setIs24Hour] = useState(false); // RESTORED from original

  // Data State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [sendMailData, setSendMailData] = useState(null);

  // UI State
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const timezoneContainerRef = useRef(null);

  // RESTORED: Exactly how Candidate ID was extracted in original
  const params = window.location.href;
  const candidateId = params.split("?")[2];
  const jobTitleParam = params.split("?")[4] ? decodeURIComponent(params.split("?")[4]) : "";

  // Handle Click Outside for Timezone Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timezoneContainerRef.current && !timezoneContainerRef.current.contains(event.target)) {
        setShowTimezoneDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initial Data Load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (candidateId) {
        try {
          const infoRes = await axios.get(`${API_BASE_URL}/api/personal_info_status/${candidateId}`);
          const info = infoRes.data;
          setCandidateInfo(info?.personal_info);
          console.log(info, "______________________")
          const recruiterEmail = info.recruiter_email || info.hr_email || DEFAULT_RECRUITER_EMAIL;
          await fetchEvents(recruiterEmail);
        } catch (err) {
          console.error("Error fetching candidate info:", err);
          await fetchEvents(DEFAULT_RECRUITER_EMAIL);
        }
      } else {
        await fetchEvents(DEFAULT_RECRUITER_EMAIL);
      }
      setLoading(false);
    };
    loadData();
  }, [candidateId]);

  const fetchEvents = async (emailId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/list-events`, {
        email_id: emailId || DEFAULT_RECRUITER_EMAIL,
        days: FETCH_DAYS
      });
      setEvents(response.data.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Logic for generating slots - CALCULATION LOGIC KEPT IDENTICAL
  const calculateSlots = (date) => {
    if (!date) return [];

    const workStart = setMinutes(setHours(startOfDay(date), WORKING_HOURS.start), 0);
    const workEnd = setMinutes(setHours(startOfDay(date), WORKING_HOURS.end), 0);

    const allPossibleStarts = eachMinuteOfInterval({
      start: workStart,
      end: workEnd
    }, { step: SLOT_INCREMENT });

    allPossibleStarts.pop();

    return allPossibleStarts.filter(slotStart => {
      const now = new Date();
      if (isBefore(slotStart, now)) return false;

      const actualSlotEnd = new Date(slotStart.getTime() + SLOT_DURATION * 60000);
      if (actualSlotEnd > workEnd) return false;

      const isBusy = events.some(event => {
        const eventStart = parseISO(event.start);
        const eventEnd = parseISO(event.end);
        return (slotStart < eventEnd && actualSlotEnd > eventStart);
      });

      return !isBusy;
    });
  };

  const availableSlots = useMemo(() => {
    return calculateSlots(selectedDate);
  }, [selectedDate, events]);

  // RESTORED: Exactly how availability was checked in original for calendar coloring
  const availableDates = useMemo(() => {
    const dates = new Set();
    const today = startOfDay(new Date());
    const maxDate = addDays(today, FETCH_DAYS - 1); // Respecting original logic

    let d = today;
    while (d <= maxDate) {
      const slots = calculateSlots(d);
      if (slots.length > 0) {
        dates.add(format(d, 'yyyy-MM-dd'));
      }
      d = addDays(d, 1);
    }
    return dates;
  }, [events]);

  const formatTimeInZone = (date, tz = selectedTimezone) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: !is24Hour // Respecting original preference
      }).format(date);
    } catch (e) {
      return format(date, is24Hour ? 'HH:mm' : 'h:mm a');
    }
  };

  const handleBookInterview = async () => {
    setLoading(true);
    try {
      const payload = {
        candidate_name: candidateInfo ? `${candidateInfo.first_name} ${candidateInfo.last_name}` : "Candidate",
        candidate_email: candidateInfo?.email || "",
        candidate_id: candidateId || "",
        job_id: candidateInfo?.job_id || "",
        position: candidateInfo?.applied_for || "Position",
        interview_level: candidateInfo?.next_round || "Technical Round",
        date: format(selectedSlot, 'yyyy-MM-dd'),
        time: formatTimeInZone(selectedSlot),
        duration_minutes: SLOT_DURATION,
        timezone: selectedTimezone,
        interviewers: [
          {
            name: candidateInfo?.recruiter_name || "Manager",
            email: candidateInfo?.recruiter_email || candidateInfo?.hr_email || DEFAULT_RECRUITER_EMAIL
          }
        ]
      };

      const response = await axios.post(`${API_BASE_URL}/api/send_email`, payload);
      setSendMailData(response.data);
      setStep('success');
      setShowConfirmPopup(false);
    } catch (error) {
      console.error('Error booking interview:', error);
      alert('Failed to schedule interview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadICS = () => {
    if (!sendMailData?.ics_content) {
      alert("Preparing your calendar invite...");
      return;
    }
    try {
      const blob = new Blob([sendMailData.ics_content], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'interview.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error downloading ICS:", e);
    }
  };

  // Render Functions
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    const today = startOfDay(new Date());
    const maxFetchDate = addDays(today, FETCH_DAYS - 1);

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dateKey = format(day, 'yyyy-MM-dd');

        // IDENTICAL DISABLED LOGIC FROM ORIGINAL
        const isDisabled = !isSameMonth(day, monthStart) || isBefore(day, today) || day > maxFetchDate;
        const isAvailable = availableDates.has(dateKey);
        const isSelected = isSameDay(day, selectedDate);
        const isToday = isSameDay(day, new Date());

        days.push(
          <button
            key={day.toString()}
            className={`day-btn ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
            disabled={isDisabled || !isAvailable}
            onClick={() => {
              setSelectedDate(cloneDay);
              setSelectedSlot(null);
            }}
          >
            <span>{format(day, 'd')}</span>
          </button>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="calendar-grid" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    return rows;
  };

  const currentTZLabel = Object.values(TIMEZONES).flat().find(tz => tz.value === selectedTimezone)?.label || selectedTimezone;

  if (step === 'success') {
    return (
      <div className="calendar-container">
        <div className="calendar-card" style={{ maxWidth: '32rem' }}>
          <div className="success-banner">
            <div className="success-icon-wrapper">
              <CheckCircle size={40} color="white" />
            </div>
            <h1>Interview Confirmed!</h1>
            <p>Your L2 interview has been successfully scheduled</p>
          </div>
          <div className="confirm-content">
            <div className="success-details-card">
              <div className="confirm-grid">
                <div className="confirm-row">
                  <span className="confirm-label">Position</span>
                  <span className="confirm-value">{candidateInfo?.applied_for || "Senior Frontend Developer"}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Date</span>
                  <span className="confirm-value">{format(selectedSlot, 'EEEE, MMMM d, yyyy')}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Time</span>
                  <span className="confirm-value">{formatTimeInZone(selectedSlot)}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Timezone</span>
                  <span className="confirm-value">{currentTZLabel}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Duration</span>
                  <span className="confirm-value">{SLOT_DURATION} minutes</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label">Mode</span>
                  <span className="confirm-value">Google Meet</span>
                </div>
              </div>
            </div>

            {/* <div className="success-actions">
              <a
                href={sendMailData?.meet_link || sendMailData?.interview?.meetLink || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="meet-link-btn"
                onClick={(e) => { if (!sendMailData?.meet_link && !sendMailData?.interview?.meetLink) e.preventDefault(); }}
              >
                <Video size={18} /> Join Google Meet
              </a>
              <button className="ics-btn" onClick={handleDownloadICS}>
                <Download size={16} /> Download Calendar File (.ics)
              </button>
            </div> */}

            <div className="success-note">
              <AlertCircle size={18} color="#1d4ed8" />
              <p className="note-text">
                <strong>Note:</strong> A confirmation email with all the details and a calendar invite has been prepared. You can download the .ics file above to add this event to your calendar.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="calendar-container">
        {showConfirmPopup && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-icon">
                <CalendarIcon size={24} color="#2563eb" />
              </div>
              <h3 className="modal-title">Confirm Interview?</h3>
              <p className="modal-desc">Are you sure you want to confirm this interview? This action cannot be undone.</p>
              <div className="modal-btns">
                <button className="back-btn" onClick={() => setShowConfirmPopup(false)}>
                  Go Back
                </button>
                <button className="book-btn" onClick={handleBookInterview} disabled={loading}>
                  {loading ? <div className="spinner" style={{ width: '1rem', height: '1rem', margin: 0, borderTopColor: 'white' }}></div> : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="calendar-card" style={{ maxWidth: '32rem' }}>
          <div className="step-indicator">
            <div className="steps-container">
              <div className="step completed"><div className="step-number"><CheckCircle size={14} /></div><span className="step-text">Select Time</span></div>
              <div className="step-divider"></div>
              <div className="step active"><div className="step-number">2</div><span className="step-text">Confirm</span></div>
            </div>
          </div>
          <div className="confirm-content">
            <h2 className="confirm-title">Confirm Your Interview</h2>
            <div className="confirm-card">
              <div className="confirm-grid">
                <div className="confirm-row">
                  <span className="confirm-label"><User size={14} /> Candidate</span>
                  <span className="confirm-value">{candidateInfo ? `${candidateInfo.first_name} ${candidateInfo.last_name}` : "Candidate"}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label"><Briefcase size={14} /> Role</span>
                  <span className="confirm-value">{jobTitleParam}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label"><CalendarIcon size={14} /> Date</span>
                  <span className="confirm-value">{format(selectedSlot, 'EEEE, MMMM d, yyyy')}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label"><Clock size={14} /> Time</span>
                  <span className="confirm-value">{formatTimeInZone(selectedSlot)}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label"><Globe size={14} /> Timezone</span>
                  <span className="confirm-value">{currentTZLabel}</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label"><Timer size={14} /> Duration</span>
                  <span className="confirm-value">{SLOT_DURATION} minutes</span>
                </div>
                <div className="confirm-row">
                  <span className="confirm-label"><Video size={14} /> Mode</span>
                  <span className="confirm-value">Google Meet</span>
                </div>
              </div>
            </div>
            <div className="confirm-actions">
              <button className="back-btn" onClick={() => setStep('select')}>
                <ArrowLeft size={16} /> Back
              </button>
              <button className="book-btn" onClick={() => setShowConfirmPopup(true)} disabled={loading}>
                Confirm Interview
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <div className="calendar-card">
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Loading your schedule...</p>
          </div>
        )}

        <div className="calendar-header-banner">
          <div className="header-content">
            <div className="header-icon-box">
              <Briefcase size={24} />
            </div>
            <div className="header-text">
              <h1>{candidateInfo?.applied_for || jobTitleParam || "Position"}</h1>
              <div className="header-meta">
                <div className="meta-item"><User size={14} /> {candidateInfo?.recruiter_name || "HR Team"}</div>
                <div className="meta-item"><Timer size={14} /> {SLOT_DURATION} min</div>
                <div className="meta-item"><Video size={14} /> Google Meet</div>
              </div>
            </div>
          </div>
        </div>

        <div className="step-indicator">
          <div className="steps-container">
            <div className="step active"><div className="step-number">1</div><span className="step-text">Select Time</span></div>
            <div className="step-divider"></div>
            <div className="step inactive"><div className="step-number">2</div><span className="step-text">Confirm</span></div>
          </div>
        </div>

        <div className="timezone-bar">
          <div className="timezone-label-wrapper">
            <Globe size={16} color="#3b82f6" />
            <span>Timezone:</span>
          </div>
          <div className="timezone-selector" ref={timezoneContainerRef}>
            <button className="timezone-btn" onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}>
              {currentTZLabel}
              <ChevronLeft size={14} style={{ transform: showTimezoneDropdown ? 'rotate(90deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
            </button>
            {showTimezoneDropdown && (
              <div className="timezone-dropdown">
                {/* 24h TOGGLE RESTORED FROM ORIGINAL */}
                <div style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>TIME FORMAT</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: !is24Hour ? '#2563eb' : '#9ca3af' }}>am/pm</span>
                    <button
                      onClick={() => setIs24Hour(!is24Hour)}
                      style={{ width: '2rem', height: '1.125rem', backgroundColor: is24Hour ? '#2563eb' : '#d1d5db', borderRadius: '1rem', border: 'none', position: 'relative', cursor: 'pointer' }}
                    >
                      <div style={{ width: '0.875rem', height: '0.875rem', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '0.125rem', left: is24Hour ? '1rem' : '0.125rem', transition: 'left 0.2s' }}></div>
                    </button>
                    <span style={{ fontSize: '0.75rem', color: is24Hour ? '#2563eb' : '#9ca3af' }}>24h</span>
                  </div>
                </div>
                <div style={{ padding: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Search timezone..."
                    value={timezoneSearch}
                    onChange={(e) => setTimezoneSearch(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}
                  />
                </div>
                {Object.entries(TIMEZONES).map(([group, zones]) => {
                  const filtered = zones.filter(z => z.label.toLowerCase().includes(timezoneSearch.toLowerCase()));
                  if (filtered.length === 0) return null;
                  return (
                    <div key={group}>
                      <div style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', backgroundColor: '#f9fafb' }}>{group}</div>
                      {filtered.map(tz => (
                        <button
                          key={tz.value}
                          className={`timezone-option ${selectedTimezone === tz.value ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedTimezone(tz.value);
                            setShowTimezoneDropdown(false);
                          }}
                        >
                          <span>{tz.label}</span>
                          <span className="timezone-offset">{tz.value}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="main-layout">
          <div className="calendar-section">
            <div className="calendar-nav">
              <button
                className="nav-arrow"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                disabled={isSameMonth(currentMonth, new Date())}
              >
                <ChevronLeft size={20} />
              </button>
              <h3 className="month-title">{format(currentMonth, 'MMMM yyyy')}</h3>
              <button className="nav-arrow" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="calendar-grid">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className="weekday-label">{d}</div>
              ))}
            </div>
            {renderCalendar()}

            <div className="calendar-legend">
              <div className="legend-item"><div className="legend-dot dot-selected"></div> Selected</div>
              <div className="legend-item"><div className="legend-dot dot-available"></div> Available</div>
              <div className="legend-item"><div className="legend-dot dot-unavailable"></div> Unavailable</div>
            </div>
          </div>

          <div className="slots-section">
            {selectedDate ? (
              <>
                <h3 className="slots-heading">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</h3>
                <p className="slots-count">{availableSlots.length} available times</p>

                <div className="slots-scroll-area">
                  {availableSlots.length > 0 ? (
                    availableSlots.map((slot, i) => (
                      <button
                        key={i}
                        className={`slot-button ${selectedSlot === slot ? 'selected' : ''}`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Clock size={14} />
                          {formatTimeInZone(slot)}
                        </div>
                        {selectedSlot === slot && <CheckCircle size={14} />}
                      </button>
                    ))
                  ) : (
                    <div className="no-slots-view">
                      <p>No slots available</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-slots-view">
                <CalendarIcon size={48} color="#d1d5db" />
                <p>Select a date</p>
                <span className="no-slots-sub">Choose an available date to see time slots</span>
              </div>
            )}
          </div>
        </div>

        <div className="calendar-footer">
          <div className="link-expiry">
            {candidateId && <span>Link expires: {format(addDays(startOfDay(new Date()), FETCH_DAYS), 'MM/dd/yyyy')}</span>}
          </div>
          <button
            className="continue-btn"
            disabled={!selectedSlot}
            onClick={() => setStep('confirm')}
          >
            Continue <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
